import { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from 'react-intl';
import TagManager from 'react-gtm-module';
import { format } from 'date-fns'
import { styled, Container, Grid, Button, AppBar, Toolbar, Box, Typography } from '@mui/material';
import * as FitterHelper from '../library/FitterHelper';
import * as GolferHelper from '../library/GolferHelper';
import * as IosHelper from '../library/IosHelper';
import Fitter from '../classes/Fitter';
import Golfer, { OrderStatus } from '../classes/Golfer';
import head_icon from '../images/head-icon.svg';

const grey = {
    50: '#F3F6F9',
    100: '#E7EBF0',
    200: '#E0E3E7',
    300: '#CDD2D7',
    400: '#B2BAC2',
    500: '#A0AAB4',
    600: '#6F7E8C',
    700: '#3E5060',
    800: '#2D3843',
    900: '#1A2027',
};

const Root = styled('div')(
    ({ theme }) => `
    table {
      font-family: 'ProximaNova', sans-serif;
      font-size: 0.875rem;
      border-collapse: collapse;
      width: 100%;
    }
  
    td,
    th {
      border: 1px solid ${theme.palette.mode === 'dark' ? grey[800] : grey[200]};
      text-align: left;
      padding: 6px;
    }
  
    th {
      background-color: ${theme.palette.mode === 'dark' ? grey[900] : grey[100]};
    }
    `,
);


export default function FittingHistory(props: any) {
    const navigate = useNavigate();
    const [fitter, setFitter] = useState(new Fitter());
    const [rows, setRows] = useState([] as Golfer[])
    const [iOS, setiOS] = useState(false);

    const intl = useIntl();
    const intlSent = intl.formatMessage({ id: "history.sent" });
    const intlInQueue = intl.formatMessage({ id: "history.inqueue" });
    const intlIncomplete = intl.formatMessage({ id: "history.incomplete" });
    const intlError = intl.formatMessage({ id: "history.error" });
    const intlWhyGrinds = intl.formatMessage({ id: "general.why.grinds" });
    const intlContinue = intl.formatMessage({ id: "general.continue" });

    useEffect((): void => {
        const getAsyncData = async () => {
            if (!(await IosHelper.get())) {
                await FitterHelper.logOutIfExpiredLogin(navigate);
            }
            let newFitter = await FitterHelper.getFitter();
            setFitter(newFitter);
            let golferList = await GolferHelper.getGolferListByFitter(newFitter.fitterID, newFitter.email);
            setRows(golferList);
            setiOS(await IosHelper.get());
        }

        getAsyncData();

    }, []);

    const handleCancel = () => {
        navigate('/apps/wedgefitting/golferinfo');
    }

    const handleWhyGrinds = async (id: number) => {
        // Check for login expiration if not iOS
        if (!iOS) {
            await FitterHelper.logOutIfExpiredLogin(navigate);
        }
        //get the row corresponding to the button click
        const row = rows.filter(r => r.id === id)[0];
        await GolferHelper.setGolferId(id);
        const golfer = await GolferHelper.getGolferById(id);
        //redirect to the appropriate page
        switch (row.orderStatus) {
            case "incomplete":
                //determine where the golfer was in the fitting process and go to that page.
                //look at which page vars are empty to determine how far they got

                if (golfer.confidence === "") {
                    sendGtmTag("incomplete - golfer preferences")
                    navigate("/apps/wedgefitting/golferpreferences");
                } else if (golfer.recommendedLofts.length === 0) {
                    sendGtmTag("incomplete - in bag");
                    navigate("/apps/wedgefitting/inbag");
                } else if (golfer.recommendedLofts.length > 0 && golfer.grindRecommendations.length === 0) {
                    sendGtmTag("incomplete - fitting")
                    navigate("/apps/wedgefitting/fitting");
                } else {
                    sendGtmTag("incomplete - why grinds")
                    navigate("/apps/wedgefitting/whygrinds");
                }
                break;
            case "queued":
                sendGtmTag("complete - why grinds - in queue")
                navigate("/apps/wedgefitting/whygrinds?queued=1");
                break;
            case "sent":
                sendGtmTag("complete - why grinds - review")
                navigate('/apps/wedgefitting/whygrinds?fitting=1');
                break;
        }
    }

    const sendGtmTag = (eventType = "") => {
        TagManager.dataLayer({
            dataLayer: {
                event: 'vwf_fitting_history',
                fit_event_status: eventType
            }
        });
    }

    const orderStatusText = (value: OrderStatus) => {
        let status = "";
        switch (value) {
            case "sent":
                status = intlSent;
                break;
            case "incomplete":
                status = intlIncomplete;
                break;
            case "queued":
                status = intlInQueue;
                break;
            case "error":
                status = intlError;
                break;
            }
        return status;
    }

    return (
        <>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }} className='navbar'>
                <AppBar position="fixed" className='appbar'>
                    <Container maxWidth="lg">
                        <Toolbar>
                            <Grid container>
                                <Grid item xs={4}>
                                    <Typography className='name' variant="h6" component="div" sx={{ display: { xs: 'none', md: 'flex' } }}>
                                        <img style={{ width: '30px', marginRight: '5px' }} src={head_icon} />{fitter.name}
                                    </Typography>
                                </Grid>
                                <Grid item xs={4}>
                                    <Typography variant="h6" component="div" className='title' sx={{ display: 'flex', marginLeft: '50px' }}>
                                        <FormattedMessage id="history.title" />
                                    </Typography>
                                </Grid>
                                <Grid item xs={4} container justifyContent="flex-end">
                                    <Button disableRipple type="submit" variant="contained" onClick={handleCancel} className='submit-button'><FormattedMessage id="general.done" /></Button>
                                </Grid>
                            </Grid>
                        </Toolbar>
                    </Container>
                </AppBar>
            </Box>

            <Container maxWidth="lg">
                <Root sx={{ maxWidth: '100%', marginTop: '75px' }}>
                    <table aria-label="custom pagination table">
                        <thead>
                            <tr>
                                <th style={{ width: "250px" }}><FormattedMessage id="history.golfer" /></th>
                                <th><FormattedMessage id="history.fitting.date" /></th>
                                <th><FormattedMessage id="history.status" /></th>
                                <th style={{ width: "150px" }}><FormattedMessage id="history.course" /></th>
                                <th style={{ width: "150px" }}><FormattedMessage id="history.bunker" /></th>
                                <th style={{ width: "150px" }}><FormattedMessage id="history.confidence" /></th>
                                <th style={{ width: "500px" }}><FormattedMessage id="history.grind.recommendations" /></th>
                                <th style={{ width: "150px" }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIx) => {
                                return (
                                    <tr key={rowIx}>
                                        <td>{row.fullName} </td>
                                        <td style={{ width: 120 }} align="right">
                                            {format(new Date(row.fittingDate), "MM/dd/yyyy")}
                                        </td>
                                        <td style={{ width: 100 }}>{orderStatusText(row.orderStatus)}</td>
                                        <td>{row.courseConditions}</td>
                                        <td>{row.bunkerConditions}</td>
                                        <td>{row.confidence}</td>
                                        <td>
                                            {row.orderStatus !== "incomplete" &&
                                                <table style={{ maxWidth: "100%", tableLayout: "fixed" }}>
                                                    <tbody>
                                                        <tr>
                                                            {row.grindRecommendations.map((grind, grindIx) => (
                                                                <td style={{ display: "inline-block", maxWidth: "70px", minWidth: "70px", border: "none", padding: "0px" }} align="right" key={grindIx}>
                                                                    {grind.loft}.{grind.selectedBounce} {grind.selectedGrind}
                                                                </td>
                                                            )
                                                            )}
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            }
                                        </td>
                                        <td><Button disableRipple variant="outlined" className='submit-button-small' onClick={() => handleWhyGrinds(row.id)}>{row.orderStatus === "incomplete" ? intlContinue : intlWhyGrinds}</Button></td>
                                    </tr>
                                )
                            }
                            )}

                        </tbody>
                    </table>
                </Root>
            </Container>

        </>
    );
}
